import React, { PropsWithChildren } from 'react';
import styled from 'styled-components';
import typescales, { Typescales } from '@/common/view/typescales';
import colors, { Colors } from '@/common/view/colors';

type TextColorType = Colors | 'inherit';
interface TextProps extends PropsWithChildren {
  className?: string;
  typo?: Typescales;
  color?: TextColorType;
  textRef?: React.MutableRefObject<HTMLDivElement | null>;
  id?: string;
  inline?: boolean;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
  required?: boolean;
  highlight?: string;
  align?: 'start' | 'center' | 'end';
  ellipsis?: boolean;
}

const StyledText = styled.div<{
  $align?: 'start' | 'center' | 'end';
  $clickable: boolean;
  $inline: boolean;
  $typescale: Typescales;
  $ellipsis: boolean;
  color: TextColorType;
}>`
  ${({ $align }) => ($align ? `justify-content: ${$align};text-align:${$align};` : '')}
  ${({ $clickable }) => ($clickable ? 'cursor: pointer;' : '')}
  ${({ $typescale }) => typescales[$typescale]};
  white-space: ${({ $ellipsis }) => ($ellipsis ? 'nowrap' : 'pre-wrap')};
  display: ${({ $inline, $ellipsis }) => {
    if ($inline) {
      return 'inline-flex';
    } else if ($ellipsis) {
      return 'block';
    } else {
      return 'flex';
    }
  }};
  align-items: center;
  overflow: ${({ $ellipsis }) => ($ellipsis ? 'hidden' : '')};
  text-overflow: ${({ $ellipsis }) => ($ellipsis ? 'ellipsis' : '')};
  color: ${({ color }) => (color === 'inherit' ? 'inherit' : colors[color])};
  svg {
    fill: currentColor;
    * {
      fill: ${({ color }) => (color === 'inherit' ? 'inherit' : colors[color])};
    }
  }
`;

const Asterisk = styled.span`
  margin-left: 4px;
  color: ${colors.red50};
`;
const Mark = styled.mark`
  font-weight: inherit;
  color: ${colors.grayBlack};
  background-color: transparent;
  padding: 0;
  position: relative;
  &:after {
    content: '';
    display: inline-block;
    height: 90%;
    width: 100%;
    position: absolute;
    background: #ffd500;
    transform: rotate(2deg);
    top: 5%;
    left: 0;
    border-radius: 20% 25% 20% 24%;
    padding: 0;
    z-index: -1;
    overflow: visible;
  }
`;

const getHighlightedTexts = (text: string, highlight: string) => {
  const parts = text.split(new RegExp(`(${highlight})`, 'gi'));
  return (
    <>
      {parts.map((part, idx) => {
        const shouldHightlight = part.toLowerCase() === highlight.toLowerCase();
        return shouldHightlight ? <Mark key={`${idx}-${part}`}>{part}</Mark> : part;
      })}
    </>
  );
};

const Text: React.FC<TextProps> = ({
  className,
  typo = 'body4',
  color = 'grayBlack',
  children,
  textRef,
  id,
  inline = false,
  onClick,
  required,
  highlight,
  align = 'start',
  ellipsis = false,
}) => {
  return (
    <StyledText
      className={className}
      $align={align}
      $clickable={!!onClick}
      $inline={inline}
      $typescale={typo}
      $ellipsis={ellipsis}
      color={color}
      ref={textRef}
      id={id}
      onClick={onClick}>
      {highlight ? getHighlightedTexts(children as string, highlight) : children}
      {required && <Asterisk>*</Asterisk>}
    </StyledText>
  );
};

export default Text;
